import type {RequirementOf} from '../../lib/types';
import {LABEL_DEFAULT_FONT_SIZE} from '../../lib/constants';
import {textBoxMinHeight} from '../../lib/element-size';
import {resolveFillMode} from '../../lib/fill-mode';
import {useFillSurface} from '../FillSurface';
import {useControlFrame, type ControlFrameKind} from './control-frame';
import {substituteVars} from '../InlineLabel';
import {SlotMarks} from '../SlotMarks';
import {type ElemProps} from './element-props';

export function TextRenderer({
  item,
  fillMode,
  fillValue,
  onFillChange,
  fillReadOnly,
  fillLocked,
  sampleData,
  textStyle,
}: ElemProps & {item: RequirementOf<'text'>}) {
  // Surface-level, not per-slot: the export is a property of the whole canvas.
  const {forExport} = useFillSurface();
  const frame = useControlFrame();
  const s = item.style;
  const tCfg = item.config;
  const inputType = tCfg.input_type ?? 'textarea';
  const nativeType =
    inputType === 'date'
      ? 'date'
      : inputType === 'time'
        ? 'time'
        : inputType === 'datetime'
          ? 'datetime-local'
          : undefined;
  const isDateLike = !!nativeType;
  /**
   * One line or two — and therefore one element or the other.
   *
   * A date, a time and a short text answer are all a single-line `<input>`
   * differing only in the `type` the browser is given; they used to be two
   * branches drawing the same tag with the same classes, which is how the
   * typography reached one of them and not the other.
   */
  const singleLine = isDateLike || inputType === 'input';
  const locked = tCfg.lock_value ?? false;
  // Resolve {{variable}} tokens against the job context (empty in the editor).
  const ctx = sampleData ?? {};
  const placeholder = substituteVars(tCfg.placeholder ?? '', ctx);
  const defaultVal = substituteVars(tCfg.default_value ?? '', ctx);
  const mode = resolveFillMode({
    fillMode,
    hasOnChange: !!onFillChange,
    fillReadOnly: !!fillReadOnly,
  });
  const controlled = mode === 'controlled' || mode === 'readonly';
  const readOnly = locked || mode === 'readonly';
  const fv = typeof fillValue === 'string' ? fillValue : '';
  /**
   * The box's floor, and the same one on all three shapes below: an editable
   * control, a read-only box, and the editor's static cell view. They each had
   * their own idea of it, so one authored element was drawn at three heights and
   * a table full of them grew between the editor and the report.
   *
   * **`fontSize` is not dressing.** The floor is derived from the type the box
   * actually renders in, so the two have to be the same number — a box sized for
   * 14pt drawing its text at 12 is the drift this module keeps repairing, only
   * inside one element. That is why `textStyle` reaches every shape below,
   * including the textarea.
   */
  const fontSize = typeof textStyle?.fontSize === 'number' ? textStyle.fontSize : undefined;
  const minHeight = textBoxMinHeight({singleLine, fontSize});
  /**
   * Grow the box to the answer, on the surfaces where the answer is settled.
   *
   * **A `<textarea>` scrolls; it does not grow.** An answer longer than two rows
   * was cut off at the bottom — a reader could scroll it on screen, but the PDF
   * rasterises this DOM, so on paper it was simply gone. Sizing to
   * `scrollHeight` is the same trick the cell editor already uses, and it keeps
   * the ELEMENT the same: still a textarea, still the same borders and padding,
   * so the read-only screen and the export stay one layout. Swapping in a div
   * instead would have made the export's pagination diverge from the screen it
   * was taken from, which is the drift this module has spent this whole branch
   * removing.
   *
   * Only where nothing is being typed. An editable box that grew under the
   * cursor would reflow the A4 page mid-sentence; there the reader can scroll,
   * and submitting settles it.
   *
   * **Settled, not merely someone else's.** `mode === 'readonly'` covers both,
   * and keying on it alone made the SAME slot grow for one party and stay fixed
   * for the other — a long crew answer taller on the client's link than on the
   * job, so one JAC paginated two ways again. Whether a box grows is a property
   * of the document, so `fillLocked` is subtracted back out: while the document
   * is open every viewer sees the same fixed box, and the export grows
   * regardless, which is what keeps the record complete.
   */
  const growToFit = (mode === 'readonly' && !fillLocked) || !!forExport;
  const fitHeight = (el: HTMLTextAreaElement | null) => {
    if (!el || !growToFit) return;
    el.style.height = 'auto';
    // Only when there is a real measurement to write. An environment with no
    // layout reports 0, and pinning the box to zero would leave nothing but the
    // floor below — which happens to look right and would hide the fact that
    // the fit never ran.
    //
    // Plus the border. `scrollHeight` leaves it out, but the box is sized
    // border-box, so the text came out 2px short of its box: every line was
    // there, behind a scrollbar that a settled answer should never show.
    if (el.scrollHeight > 0) {
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    }
  };
  const bind = controlled
    ? {value: fv, onChange: (e: {target: {value: string}}) => onFillChange?.(e.target.value)}
    : {defaultValue: defaultVal};

  /** Every shape of this box wears the cell's type and stands on the same floor. */
  const boxStyle = {...textStyle, minHeight};
  /** …and the same frame, chosen by what the box IS — see `useControlFrame`. */
  const boxClass = (kind: ControlFrameKind, extra = '') =>
    `w-full rounded text-xs ${frame(kind)} ${extra}`.trim();

  const control = !fillMode ? (
    <div
      style={boxStyle}
      className={boxClass(
        'closed',
        `px-2 py-1 ${defaultVal ? 'text-gray-700' : 'text-gray-400 italic'}`,
      )}
    >
      {defaultVal ||
        (isDateLike
          ? {date: 'Date', time: 'Time', datetime: 'Date & time'}[
              inputType as 'date' | 'time' | 'datetime'
            ]
          : placeholder)}
    </div>
  ) : singleLine ? (
    <input
      type={nativeType}
      {...bind}
      // A date input draws its own format hint; a placeholder would sit under it.
      placeholder={isDateLike || locked ? undefined : placeholder}
      readOnly={readOnly}
      style={boxStyle}
      className={boxClass(
        locked ? 'closed' : 'open',
        `px-2 py-1 ${locked ? 'text-gray-500 cursor-not-allowed' : ''}`,
      )}
    />
  ) : (
    <textarea
      {...bind}
      ref={fitHeight}
      placeholder={locked ? undefined : placeholder}
      readOnly={readOnly}
      rows={2}
      // `minHeight` rather than `height` once it grows, so the floor still holds
      // an empty answer at the size the layout expects.
      style={{...textStyle, ...(growToFit ? {minHeight} : {height: minHeight})}}
      className={boxClass(
        locked ? 'closed' : 'open',
        `p-1 resize-none ${locked ? 'text-gray-500 cursor-not-allowed' : ''}`,
      )}
    />
  );

  /**
   * The input's own caption, and where it sits.
   *
   * Top and bottom stack, so the caption adds its line to the element's height;
   * left and right share the line and take width from the control instead. The
   * control keeps `flex-1` either way, so it fills whatever is left rather than
   * shrinking to its content — a text box that shrank to its placeholder would
   * be a different box on a filled report than on an empty one.
   */
  const labelText = substituteVars(tCfg.label ?? '', ctx);
  const labelPos = tCfg.label_position ?? 'top';
  const stacked = labelPos === 'top' || labelPos === 'bottom';
  const caption = labelText ? (
    <span
      className={`font-medium text-gray-600 ${stacked ? 'block' : 'shrink-0'}`}
      style={{...textStyle, fontSize: tCfg.label_font_size ?? LABEL_DEFAULT_FONT_SIZE}}
    >
      {labelText}
    </span>
  ) : null;

  /**
   * The control's own line: the box, its unit, and a caption sharing the line.
   *
   * A side caption sits on this row rather than in a wrapper around it, so the
   * three things on one line are three children of one flex row — placing them
   * used to cost three nested wrappers and two `flex-1`s, because the caption's
   * row was added around the suffix's row instead of merged with it. Outside the
   * suffix, because a caption names the whole field including its unit: "Output
   * … 12 kW", never "Output … 12 … kW".
   */
  const row = (
    <div className="flex items-center gap-1.5">
      {labelPos === 'left' && caption}
      <div className="flex-1">{control}</div>
      {tCfg.suffix && !isDateLike && (
        <span className="text-xs text-gray-600 font-medium shrink-0">{tCfg.suffix}</span>
      )}
      {labelPos === 'right' && caption}
    </div>
  );

  return (
    <div
      style={{
        opacity: s.opacity ?? undefined,
        backgroundColor: s.background_color ?? undefined,
        // Blank spans whatever holds this. A width is what gives the box free
        // space around it, and therefore something for an alignment to move it
        // within — see `cellItemStyle`.
        width: tCfg.width,
      }}
      className="relative"
    >
      <SlotMarks required={item.required} config={tCfg} />
      {/* A stacked caption is the only thing that needs a wrapper at all; with
          none — every element authored before captions existed — the row above
          is the whole element. */}
      {stacked && caption ? (
        <div className="flex flex-col gap-0.5">
          {labelPos === 'top' && caption}
          {row}
          {labelPos === 'bottom' && caption}
        </div>
      ) : (
        row
      )}
    </div>
  );
}
